import { h, Component } from 'preact';
import util from 'preact-util';
import { Text } from 'preact-i18n';

import ForceLogoutUserButton from './forceLogoutUserButton';

/*
 * Super-admin drawer: log out every user of one clinic (customer) on all devices, or one user at a time.
 *
 * Flow: preview (GET /api/sessions/admin/customer/:id) -> warning checkbox -> the red button
 * arms on the first click and fires on the second (POST /api/sessions/admin/revoke-customer).
 * Every row except the acting admin also carries a per-user <ForceLogoutUserButton>
 * (POST /api/sessions/admin/revoke-user); after one fires, the preview is refreshed quietly.
 * Rendered inside the bottom <Drawer>; registered as 'forceLogoutCustomer' in footer/index.js.
 */

function formatDateTime(value) {
    if (!value) {
        return '';
    }
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) {
        return '';
    }
    return date.toLocaleString('nb-NO', { dateStyle: 'short', timeStyle: 'short' });
}

export function canArm({ preview, confirmed, submitting, result }) {
    return !!preview && !!confirmed && !submitting && !result;
}

class ForceLogoutDrawer extends Component {
    constructor(props) {
        super(props);
        this.state = {
            loading: true,
            preview: null,
            error: null,
            confirmed: false,
            armed: false,
            submitting: false,
            result: null,
        };
    }

    componentDidMount() {
        this.loadPreview();
    }

    loadPreview = async ({ silent = false } = {}) => {
        const { customer = {} } = this.props;
        this.setState({ loading: !silent, error: null });
        const response = await util.fetchApi(`/api/sessions/admin/customer/${customer.id}`, { method: 'GET' }, {});
        if (response && response.status === 200) {
            this.setState({ loading: false, preview: response.data });
        } else {
            this.setState({ loading: false, error: (response && response.message) || 'Could not load the users of this clinic.' });
        }
    }

    // A single user was logged out from their row: refresh the session counts without
    // blanking the table behind a spinner.
    onUserLoggedOut = () => {
        this.loadPreview({ silent: true });
    }

    toggleConfirmed = () => {
        this.setState(prev => ({ confirmed: !prev.confirmed, armed: false }));
    }

    arm = () => {
        this.setState({ armed: true });
    }

    disarm = () => {
        this.setState({ armed: false });
    }

    close = () => {
        const { stores, drawerLevel } = this.props;
        stores.appState.toggleDrawer(false, drawerLevel);
    }

    submit = async () => {
        const { stores, customer = {}, onDone } = this.props;
        const { appState } = stores;
        this.setState({ submitting: true, error: null });
        const response = await util.fetchApi('/api/sessions/admin/revoke-customer', { method: 'POST' }, {
            customerId: customer.id,
            confirmed: true,
        });
        if (response && response.status === 200) {
            const result = response.data || {};
            appState.addToast({
                type: 'success',
                message: `${result.usersAffected} brukere hos ${customer.name} er logget ut (${result.sessionsRevoked} sesjoner).`,
            }, 6000);
            this.setState({ submitting: false, armed: false, result });
            if (onDone) {
                onDone(result);
            }
        } else {
            const message = (response && response.message) || 'Kunne ikke logge ut brukerne.';
            appState.addToast({ type: 'error', message, icon: 'triangle-exclamation' }, 8000);
            this.setState({ submitting: false, armed: false, error: message });
        }
    }

    render() {
        const { customer = {}, stores = {} } = this.props;
        const {
            loading, preview, error, confirmed, armed, submitting, result,
        } = this.state;
        const users = (preview && preview.users) || [];
        const otherMemberIds = (preview && preview.otherMemberIds) || [];
        // The backend refuses to revoke the acting admin, so never offer the button on that row.
        const currentUserId = (stores.userStore && stores.userStore.user && stores.userStore.user.id) || 0;

        return (
            <div class='w-100 d-flex justify-content-center position-relative'>
                <div class='w-100 px-3' style='max-width: 960px; padding-top: 20px; padding-bottom: 100px;'>
                    <h5 class='mb-1'>
                        <i class='fa-solid fa-right-from-bracket text-danger me-2' />
                        <Text id='admin.forceLogout.title'>Log out all users</Text>
                    </h5>
                    <div class='text-body-secondary mb-3'>{customer.name}</div>

                    <div class='alert alert-warning'>
                        <Text id='admin.forceLogout.warning' fields={{ name: customer.name }}>
                            Every employee at {customer.name} is logged out on all devices immediately and has to log in again. Pending login codes are deleted. Your own session is never touched.
                        </Text>
                    </div>

                    {loading && (
                        <div class='text-center py-3'>
                            <i class='fa-solid fa-spinner fa-spin me-2' />
                            <Text id='admin.forceLogout.loading'>Loading users...</Text>
                        </div>
                    )}
                    {error && <div class='alert alert-danger'>{error}</div>}

                    {preview && (
                        <div class='table-responsive mb-3'>
                            <table class='table table-sm table-striped'>
                                <thead>
                                    <tr>
                                        <th><Text id='input.user'>User</Text></th>
                                        <th class='text-end'><Text id='admin.forceLogout.activeSessions'>Active sessions</Text></th>
                                        <th><Text id='admin.forceLogout.lastActive'>Last active</Text></th>
                                        <th class='text-center'><Text id='admin.forceLogout.column'>Log out</Text></th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {users.map(u => (
                                        <tr key={u.id}>
                                            <td>
                                                <strong>{u.firstname} {u.lastname}</strong>
                                                {u.cellphone && <small class='text-body-secondary ms-2'>{u.cellphone}</small>}
                                                {u.legacyTokenSuspected && (
                                                    <span class='badge bg-warning text-dark ms-2'>
                                                        <Text id='admin.forceLogout.legacyToken'>Old-style token</Text>
                                                    </span>
                                                )}
                                            </td>
                                            <td class='text-end'>{u.activeSessions}</td>
                                            <td>{formatDateTime(u.lastSessionActive)}</td>
                                            <td class='text-center'>
                                                {!result && u.id !== currentUserId && (
                                                    <ForceLogoutUserButton
                                                        key={`force-logout-${u.id}`}
                                                        stores={stores}
                                                        user={u}
                                                        onDone={this.onUserLoggedOut}
                                                    />
                                                )}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                                <tfoot>
                                    <tr>
                                        <th><Text id='admin.forceLogout.total'>Total</Text></th>
                                        <th class='text-end'>{preview.totalActiveSessions}</th>
                                        <th />
                                        <th />
                                    </tr>
                                </tfoot>
                            </table>
                            {otherMemberIds.length > 0 && (
                                <small class='text-body-secondary'>
                                    <Text id='admin.forceLogout.otherMembers' fields={{ ids: otherMemberIds.join(', ') }}>
                                        Members whose home clinic is another customer are not included: {otherMemberIds.join(', ')}
                                    </Text>
                                </small>
                            )}
                        </div>
                    )}

                    {result ? (
                        <div class='alert alert-success'>
                            <Text id='admin.forceLogout.result' fields={{ users: result.usersAffected, sessions: result.sessionsRevoked }}>
                                {result.usersAffected} users logged out, {result.sessionsRevoked} sessions revoked.
                            </Text>
                            {result.skippedSelf && (
                                <div><small><Text id='admin.forceLogout.skippedSelf'>Your own user was skipped.</Text></small></div>
                            )}
                        </div>
                    ) : (
                        <div class='form-check mb-3'>
                            <input
                                class='form-check-input'
                                type='checkbox'
                                id='forceLogoutConfirm'
                                checked={confirmed}
                                disabled={!preview || submitting}
                                onChange={this.toggleConfirmed}
                            />
                            <label class='form-check-label' for='forceLogoutConfirm'>
                                <Text id='admin.forceLogout.confirmLabel' fields={{ name: customer.name }}>
                                    I understand that every employee at {customer.name} is logged out now and must log in again.
                                </Text>
                            </label>
                        </div>
                    )}

                    <div class='d-flex flex-row gap-2'>
                        {!result && !armed && (
                            <button type='button' class='btn btn-danger rounded-pill' disabled={!canArm(this.state)} onClick={this.arm}>
                                <i class='fa-solid fa-right-from-bracket me-1' />
                                <Text id='admin.forceLogout.button'>Log out all users</Text>
                            </button>
                        )}
                        {!result && armed && (
                            <button type='button' class='btn btn-danger rounded-pill' disabled={submitting} onClick={this.submit}>
                                {submitting ? <i class='fa-solid fa-spinner fa-spin me-1' /> : <i class='fa-solid fa-triangle-exclamation me-1' />}
                                <Text id='admin.forceLogout.confirmButton'>Are you sure? Click to log out everyone</Text>
                            </button>
                        )}
                        {!result && armed ? (
                            <button type='button' class='btn btn-outline-secondary rounded-pill' disabled={submitting} onClick={this.disarm}>
                                <Text id='admin.forceLogout.cancel'>Cancel</Text>
                            </button>
                        ) : (
                            <button type='button' class='btn btn-outline-secondary rounded-pill' onClick={this.close}>
                                <Text id='admin.forceLogout.done'>Done</Text>
                            </button>
                        )}
                    </div>
                </div>
            </div>
        );
    }
}

export default ForceLogoutDrawer;
